import React, {
  useState,
  useCallback,
  useImperativeHandle,
  useMemo,
  useEffect,
} from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
} from '@material-ui/core';
import { isEqual, isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import { t, canURL } from '~/helpers';

const Modal = React.forwardRef((props, ref) => {
  const {
    title,
    component: Component,
    actions,
    fullScreen,
    maxWidth,
    maxHeight,
    loading,
    onClose,
    disableBackdropClick,
    disableEscapeKeyDown,
    form,
    onForm,
    noPadding,
    color,
    textColor,
    labelConfirm,
    labelCancell,
  } = props;

  const [visible, setVisible] = useState(false);
  const [params, setParams] = useState({});
  const [sending, setSending] = useState(false);
  const [disabled, setDisabled] = useState(false);




  const permission = isEqual(canURL(), { read: true });

  const isStyles = useMemo(
    () => ({
      backgroundColor: color,
      color: textColor,
      ...(noPadding ? { padding: 0 } : {}),
      ...(!isEmpty(maxHeight) ? { maxHeight } : {}),
    }),
    [color, textColor, noPadding, maxHeight]
  );


  // open modal
  const handleOpenModal = useCallback((data) => {
    setVisible(true);
    setParams(data);
  }, []);

  // close modal
  const handleCloseModal = useCallback(() => {
    setVisible(false);
    onClose(params);
    setParams({});
  }, [params]);

  // submit form
  const handleSubmitForm = useCallback(() => setSending(true), []);

  // render buttons footer
  const actionsButton = useCallback(
    () =>
      actions.map((action) => (
        <Button
          key={action.label}
          color={action.color || 'primary'}
          autoFocus={!!action.autoFocus}
          onClick={() => action.callback()}
          disabled={!!action.disabled}
          startIcon={action.startIcon}
          endIcon={action.endIcon}
          variant={action.variant || 'text'}
        >
          {action.label}
        </Button>
      )),
    [actions]
  );

  useImperativeHandle(ref, () => ({
    handleOpenModal,
    handleCloseModal,
  }));

  React.useEffect(() => {
    if (sending) setSending(false);
  }, [sending]);

  return (
    <Dialog
      fullWidth
      open={visible}
      onClose={handleCloseModal}
      fullScreen={!!fullScreen}
      maxWidth={maxWidth}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      disableBackdropClick={disableBackdropClick}
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      {loading && <LinearProgress />}

      {title && (
        <DialogTitle
          id="alert-dialog-title"
          style={{ backgroundColor: color, color: textColor }}
        >
          {title}
        </DialogTitle>
      )}

      <DialogContent style={isStyles}>
        <Component
          {...params}
          close={handleCloseModal}
          sending={sending}
          visible={visible}
          onForm={onForm}
          toggleDisabled={setDisabled}
        />
      </DialogContent>

      <DialogActions style={{ backgroundColor: color }}>
        {form && !permission && (
          <Button
            onClick={handleSubmitForm}
            color="primary"
            disabled={loading || disabled}
          >
            {t(labelConfirm)}
          </Button>
        )}
        {(!!fullScreen && !actions) ||
          (form && (
            <Button onClick={handleCloseModal} color="secondary">
              {t(labelCancell)}
            </Button>
          ))}
        {!permission && actionsButton()}
      </DialogActions>
    </Dialog>
  );
});

Modal.propTypes = {
  title: PropTypes.string,
  component: PropTypes.oneOfType([PropTypes.element, PropTypes.func])
    .isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      callback: PropTypes.func.isRequired,
      color: PropTypes.string,
      autoFocus: PropTypes.bool,
      disabled: PropTypes.bool,
      variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
      startIcon: PropTypes.node,
      endIcon: PropTypes.node,
    })
  ),
  fullScreen: PropTypes.bool,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  maxHeight: PropTypes.string,
  loading: PropTypes.bool,
  onClose: PropTypes.func,
  disableBackdropClick: PropTypes.bool,
  disableEscapeKeyDown: PropTypes.bool,
  form: PropTypes.bool,
  onForm: PropTypes.func,
  noPadding: PropTypes.bool,
  color: PropTypes.string,
  textColor: PropTypes.string,
  labelConfirm: PropTypes.string,
  labelCancell: PropTypes.string,
};

Modal.defaultProps = {
  title: '',
  actions: [],
  fullScreen: false,
  maxWidth: 'md',
  maxHeight: '',
  loading: false,
  onClose: () => undefined,
  disableBackdropClick: false,
  disableEscapeKeyDown: false,
  form: false,
  onForm: () => undefined,
  noPadding: false,
  color: '#FFF',
  textColor: 'rgba(0, 0, 0, 0.87)',
  labelConfirm: 's.geral.botaoconfirmar',
  labelCancell: 's.geral.botaofechar',
};

export default Modal;
